import { getOspreyApiBaseUrl } from '../../../shared/utils/apiBase';
import type {
  ContractPreview,
  OnboardingTaxonomy,
  SelfOnboardPayload,
  SelfOnboardResult,
  SignContractPayload,
  SignContractResult,
} from '../types';

// Public merchant self-onboarding contract flow (PE-2097).
//
// These endpoints are @Public on watchman and reached WITHOUT a session, so we
// deliberately do NOT use WatchmanFetch (it attaches the auth token and, on 401,
// logs out + redirects to /login — wrong for an anonymous, token-in-URL page).
// Instead we hit the osprey-api iam-proxy directly (<base>/iam/<rest> -> watchman
// /api/v1/<rest>), exactly like authApiClient's forgot-password / claim flows.
const getIamBaseUrl = (): string => getOspreyApiBaseUrl() + '/iam';

/** Prefer watchman's human message (e.g. "Contract link is invalid or has expired") over a generic one. */
async function readError(res: Response): Promise<never> {
  const err = await res.json().catch(() => ({} as Record<string, unknown>));
  const rawMsg = (err as { message?: unknown }).message;
  const message = Array.isArray(rawMsg)
    ? (rawMsg[0] as string) || res.statusText
    : (rawMsg as string) || res.statusText || 'Something went wrong';
  throw { status: res.status, message, fullError: err };
}

export const selfOnboardApi = {
  /**
   * GET /merchants/self-onboard/taxonomy — industries + business categories for
   * the sign-up form. Unauthenticated and active-only.
   */
  getTaxonomy: async (): Promise<OnboardingTaxonomy> => {
    const res = await fetch(`${getIamBaseUrl()}/merchants/self-onboard/taxonomy`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) await readError(res);
    return res.json();
  },

  /**
   * POST /merchants/self-onboard — submit the application. 201.
   *
   * No password is set and no session is created: watchman emails a single-use
   * contract link, and the account only becomes usable once TCS approves. 409
   * when an application with the same business name is already in progress.
   *
   * ⚠️ `bankDetails.bankCode` is captured as free text. osprey-api DOES expose a
   * bank picker at GET /payout-destinations/banks, but that controller carries
   * CollectiveOnlyGuard + PAYOUT_DESTINATION.READ, so it is unreachable from this
   * public form. A public banks lookup would let the field become a dropdown.
   */
  submit: async (payload: SelfOnboardPayload): Promise<SelfOnboardResult> => {
    const res = await fetch(`${getIamBaseUrl()}/merchants/self-onboard`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) await readError(res);
    return res.json();
  },

  /** GET /merchants/self-onboard/:merchantId/contract?token= — the template to review before signing. */
  getContractForSigning: async (merchantId: string, token: string): Promise<ContractPreview> => {
    const url = `${getIamBaseUrl()}/merchants/self-onboard/${encodeURIComponent(merchantId)}/contract?token=${encodeURIComponent(token)}`;
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) await readError(res);
    return res.json();
  },

  /** POST /merchants/self-onboard/:merchantId/contract/sign — records the auditable signing event. */
  signContract: async (merchantId: string, payload: SignContractPayload): Promise<SignContractResult> => {
    const url = `${getIamBaseUrl()}/merchants/self-onboard/${encodeURIComponent(merchantId)}/contract/sign`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) await readError(res);
    return res.json();
  },
};
